import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { motion } from 'framer-motion';
import { CheckCircle, Package, Clock } from 'lucide-react';
import axios from 'axios';
import { FlowBackground } from '../components/home/FlowBackground';
import { PAGE_BG } from '../components/home/glass';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderConfirmation() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    const { lang } = useLanguage();
    const [status, setStatus] = useState('loading');
    const [attempts, setAttempts] = useState(0);

    useEffect(() => {
        if (!sessionId) { setStatus('error'); return; }
        pollStatus();
    }, [sessionId]);

    const pollStatus = async (attempt = 0) => {
        if (attempt >= 6) { setStatus('timeout'); return; }
        try {
            const res = await axios.get(`${API}/checkout/status/${sessionId}`);
            const data = res.data;
            if (data.payment_status === 'paid' || data.status === 'completed') {
                setStatus('success');
            } else if (data.status === 'expired') {
                setStatus('cancelled');
            } else {
                setAttempts(attempt + 1);
                setTimeout(() => pollStatus(attempt + 1), 2000);
            }
        } catch {
            setAttempts(attempt + 1);
            setTimeout(() => pollStatus(attempt + 1), 2000);
        }
    };

    return (
        <div>
            <SEO title="Order Confirmation | Sleepstack" path="/order/confirmation" />
            <PreSaleBanner />
            <Navbar />
            <main className="relative isolate glass-scope min-h-screen" style={{ paddingTop: 'var(--page-offset, 128px)', background: PAGE_BG }}>
                <FlowBackground variant="order" />
                <div className="section-container py-20">
                    <div className="max-w-lg mx-auto">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4 }}
                            className="card-base p-10 text-center"
                            data-testid="order-confirmation"
                        >
                            {status === 'loading' && (
                                <>
                                    <div className="w-14 h-14 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
                                    <h2 className="heading-sm text-foreground mb-2">Verifying payment...</h2>
                                    <p className="text-muted-foreground text-sm">Please wait while we confirm your order.</p>
                                </>
                            )}

                            {status === 'success' && (
                                <>
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <CheckCircle size={32} className="text-green-600" />
                                    </div>
                                    <h1 className="heading-md text-foreground mb-2">Order confirmed!</h1>
                                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                                        Thank you for your pre-order. Your Sleepstack sleep powder is expected to ship in{' '}
                                        <strong>November/December 2026</strong>, we only ship once it has passed every quality check. A confirmation email is on its way.
                                    </p>

                                    <div className="grid grid-cols-2 gap-4 mb-8">
                                        <div className="bg-accent rounded-xl p-4">
                                            <Package size={20} className="text-primary mx-auto mb-2" />
                                            <div className="text-xs text-muted-foreground">Order status</div>
                                            <div className="text-sm font-semibold text-green-600">Confirmed</div>
                                        </div>
                                        <div className="bg-accent rounded-xl p-4">
                                            <Clock size={20} className="text-primary mx-auto mb-2" />
                                            <div className="text-xs text-muted-foreground">Ships</div>
                                            <div className="text-sm font-semibold">Expected November/December 2026</div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <Link to="/dashboard" data-testid="view-order-btn" className="btn-secondary w-full block text-center">
                                            View my account
                                        </Link>
                                        <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors block">
                                            Back to home
                                        </Link>
                                    </div>
                                </>
                            )}

                            {(status === 'cancelled' || status === 'error') && (
                                <>
                                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <span className="text-2xl">✗</span>
                                    </div>
                                    <h2 className="heading-sm text-foreground mb-2">{lang === 'nl' ? 'Betaling niet voltooid' : 'Payment not completed'}</h2>
                                    <p className="text-muted-foreground text-sm mb-6">{lang === 'nl' ? 'Je betaling is niet voltooid. Er is niets afgeschreven.' : 'Your payment was not completed. No charge was made.'}</p>
                                    <Link to="/checkout" className="btn-primary">{lang === 'nl' ? 'Opnieuw proberen' : 'Try again'}</Link>
                                </>
                            )}

                            {status === 'timeout' && (
                                <>
                                    <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <Clock size={28} className="text-amber-600" />
                                    </div>
                                    <h2 className="heading-sm text-foreground mb-2">Payment processing...</h2>
                                    <p className="text-muted-foreground text-sm mb-6">
                                        Your payment is being processed. Check your email for confirmation, or view your account.
                                    </p>
                                    <Link to="/dashboard" className="btn-secondary">View my account</Link>
                                </>
                            )}
                        </motion.div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
